import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatCheckbox } from '@angular/material/checkbox';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { ToastService } from '../../services/toast.service';
import { ClientInformation } from '../../models/client-information';
import { Integration } from '../../models/integration';
import { User } from '../../models/user';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { IntegrationMaintenanceService } from '../../services/integration-maintenance.service';
import { ServerService } from 'src/app/services/server.service';
import { IntegrationMaintenance } from '../../models/integration-maintenance';
import { Server } from '../../models/server';
import { DestinationEndpoint } from '../../models/destination-endpoint';
import { ImportType } from '../../models/import-type';
import { ReportingUtils } from 'src/app/utilities/reporting-utilities';
import { IntegrationMaintenanceFilters } from 'src/app/models/integration-maintenance-filters';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';

@Component({
  selector: 'app-edit-integration-maintenance',
  templateUrl: './edit-integration-maintenance.component.html',
  styleUrls: ['./edit-integration-maintenance.component.scss'],
  providers: [ClientIntegrationService, TypeConstantService, ServerService, IntegrationMaintenanceService],
  standalone: false,
})
export class EditIntegrationMaintenanceComponent implements OnInit, OnDestroy {
  @ViewChild('filterCheckbox') public filterCheckbox: MatCheckbox;
  @ViewChild('editIntegrationGrid') public editIntegrationGrid: LTPngTableComponent<Integration>;
  @Input() clients: ClientInformation[];
  @Input() integrations: Integration[];
  @Input() importTypes: ImportType[];
  @Input() servers: Server[];
  @Input() destinationEndpoints: DestinationEndpoint[];
  @Input() maintenance: IntegrationMaintenance;
  @Output() outputMaintenance = new EventEmitter<IntegrationMaintenance>();
  @Output() deletedMaintenance = new EventEmitter<IntegrationMaintenance>();
  componentDestroyed$ = new Subject<boolean>();

  clientItems: ClientInformation[] = [];
  selectedClients: ClientInformation[] = [];
  selectedServers: Server[] = [];
  selectedDestinationEndpoints: DestinationEndpoint[] = [];
  selectedImportTypes: ImportType[] = [];
  public selectedIntegrationIds: string[] = []; // selected integrations in grid
  onlyShowSelectedIntegrations = false;

  reportingUtils: ReportingUtils;
  filteredIntegrations: Integration[] = [];
  public gettingServerItems = true;

  integrationGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  startTimeDateInput: Date;
  stopTimeDateInput: Date;
  adding = true;
  pastMaintenance = false;

  savingMaintenance = false;
  pageLoading = true;

  constructor(
    private clientService: ClientIntegrationService,
    private typeConstantService: TypeConstantService,
    private serverService: ServerService,
    private toastService: ToastService,
    private router: Router,
    private maintenanceService: IntegrationMaintenanceService,
    private user: User,
    private route: ActivatedRoute,
  ) {
    this.reportingUtils = new ReportingUtils();
  }

  ngOnInit(): void {
    this.integrationGridSettings.set(this.setupGrid());

    this.clientItems = this.clients;
    if (this.maintenance != null) {
      this.adding = false;
      this.initializeMaintenance();
    }
    else {
      this.startTimeDateInput = new Date();
      this.pageLoading = false;
    }
    this.loadDataIntoGridView();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  initializeMaintenance(): void {
    this.startTimeDateInput = new Date(this.maintenance.maintenanceStartTime);
    this.stopTimeDateInput = new Date(this.maintenance.maintenanceStopTime);
    this.selectedClients = this.clients.filter(c => this.maintenance.maintenanceFilters.clientInformationIds.includes(c.clientInformationId));
    this.selectedDestinationEndpoints = this.destinationEndpoints.filter(d => this.maintenance.maintenanceFilters.destinationEndpointIds.includes(d.destinationEndpointId));
    this.selectedImportTypes = this.importTypes.filter(i => this.maintenance.maintenanceFilters.importTypeIds.includes(i.importTypeId));
    this.selectedServers = this.servers.filter(s => this.maintenance.maintenanceFilters.serverIds.includes(s.serverId));
    this.selectedIntegrationIds = this.maintenance.maintenanceFilters.integrationIds;

    const dateNow = new Date();
    if (!this.maintenance.isActive || new Date(this.maintenance.maintenanceStopTime) < dateNow) {
      this.pastMaintenance = true;
    }

    this.pageLoading = false;
  }

  saveMaintenance(): void {
    this.savingMaintenance = true;
    const newMaintenance = new IntegrationMaintenance();
    newMaintenance.maintenanceFilters = new IntegrationMaintenanceFilters();
    newMaintenance.maintenanceFilters.integrationIds = this.selectedIntegrationIds;
    newMaintenance.maintenanceFilters.destinationEndpointIds = this.selectedDestinationEndpoints.map(d => d.destinationEndpointId);
    newMaintenance.maintenanceFilters.importTypeIds = this.selectedImportTypes.map(i => i.importTypeId);
    newMaintenance.maintenanceFilters.clientInformationIds = this.selectedClients.map(c => c.clientInformationId);
    newMaintenance.maintenanceFilters.serverIds = this.selectedServers.map(s => s.serverId);
    newMaintenance.maintenanceStartTime = this.startTimeDateInput;
    newMaintenance.maintenanceStopTime = this.stopTimeDateInput;
    newMaintenance.isActive = true;

    if (this.adding) {
      this.maintenanceService.insertMaintenance(newMaintenance)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (newId) => {
            this.toastService.toastCreate('Integration Maintenance ' + newId + ' Added.', 'Success');
            this.maintenanceService.getMaintenance(newId).subscribe({
              next: (output) => {
                this.outputMaintenance.emit(output);
                this.savingMaintenance = false;
              },
              error: (_error) => {
                this.toastService.toastCreate('Error Adding Maintenance', 'Danger');
                this.savingMaintenance = false;
              },
            });
          },
          error: (_error) => {
            this.toastService.toastCreate('Error Adding Maintenance', 'Danger');
            this.savingMaintenance = false;
          },
        });
    }
    else {
      newMaintenance.integrationMaintenanceId = this.maintenance.integrationMaintenanceId;
      this.maintenanceService.updateMaintenance(newMaintenance)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (_value) => {
            this.toastService.toastCreate('Integration Maintenance ' + this.maintenance.integrationMaintenanceId + ' Updated.', 'Success');
            this.maintenanceService.getMaintenance(this.maintenance.integrationMaintenanceId).subscribe(
              (output) => {
                this.outputMaintenance.emit(output);
              },
            );
            this.savingMaintenance = false;
          },
          error: (_error) => {
            this.toastService.toastCreate('Error Updating Maintenance', 'Danger');
            this.savingMaintenance = false;
          },
        });
    }
  }

  deleteMaintenance(): void {
    this.savingMaintenance = true;
    if (window.confirm('Are you sure you want to delete this maintenance?')) {
      this.maintenanceService.deleteMaintenance(this.maintenance.integrationMaintenanceId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (_value) => {
            this.toastService.toastCreate('Integration Maintenance ' + this.maintenance.integrationMaintenanceId + ' Deleted.', 'Success');
            this.deletedMaintenance.emit(this.maintenance);
            this.savingMaintenance = false;
          },
          error: (_error) => {
            this.toastService.toastCreate('Error Deleting Maintenance', 'Danger');
            this.savingMaintenance = false;
          },
        });
    }
  }

  filterClients(searchTerm: string): void {
    this.clientItems = this.clients.filter(c => c.clientDisplayName.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  loadDataIntoGridView() {
    this.integrationGridSettings().loading.set(true);

    this.filteredIntegrations = this.integrations;
    if (this.onlyShowSelectedIntegrations) {
      this.filteredIntegrations = [...this.editIntegrationGrid.getSelectedItems()];
    }
    this.integrationGridSettings.set(this.integrationGridSettings().setData(this.filteredIntegrations));

    this.integrationGridSettings().loading.set(false);
  }

  /* Angular Material Functions */

  displayFnClient(x: ClientInformation): string {
    return x && x.clientDisplayName ? x.clientDisplayName : '';
  }

  displayFnIntegration(x: Integration): string {
    return x && x.name ? x.name : '';
  }

  public expandedDetailKeys: string[] = [];

  public expandDetailsBy = (dataItem: Integration): string => {
    return dataItem.integrationId;
  };

  public checkIfIdExist(id: string): number {
    return this.expandedDetailKeys.findIndex((i) => i === id);
  }

  setupGrid(): GridSettings {
    return new GridSettings({
      selection: {
        enabled: true,
        selectionType: 'checkbox',
        mode: 'multiple',
      },
      loading: this.gridLoading,
      dataKey: 'integrationId',
      data: this.filteredIntegrations,
      fixedGrid: true,
      columns: [  
        new GridColumnData({ width: '300px', field: 'name', header: 'Name'}),
        new GridColumnData({ width: '200px', field: 'dataSourceTypeId', header: 'DataSource'}),
        new GridColumnData({ width: '150px', field: 'serverId', header: 'Server'}),
        new GridColumnData({ width: '150px', field: 'locked', header: 'Locked'}),
        new GridColumnData({ filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ width: '150px', field: 'ipRestricted', header: 'IP Restricted'}),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: true,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
    });
  }

  selectionChangeHandler(): void {
    this.selectedIntegrationIds = this.editIntegrationGrid.getSelectedItems().map(i => i.integrationId);
  }

}
